import apiClient, { handleUnauthorizedResponse } from "./apiClient";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:4000";

/**
 * Documents Service
 * Frontend API client for sales documents management
 */

export interface DocumentFilters {
    outletId?: string
    registerId?: string
    documentType?: string
    status?: string
    number?: string
    customerId?: string
    customerSearch?: string
    dateFrom?: string
    dateTo?: string
    searchText?: string
    limit?: number
    offset?: number
}

export async function searchDocuments(
  companyId: string,
  filters: DocumentFilters = {},
) {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, value.toString());
    }
  });

  const response = await apiClient.get(
    `/api/companies/${companyId}/documents?${params.toString()}`,
  );
  return response;
}

export async function getDocument(companyId: string, documentId: string) {
  const response = await apiClient.get(
    `/api/companies/${companyId}/documents/${documentId}`,
  );
  return { document: response.document, lines: response.lines || [], globalDiscount: response.globalDiscount || null };
}

export async function createDocument(companyId: string, documentData: any) {
  const response = await apiClient.post(
    `/api/companies/${companyId}/documents`,
    documentData,
  );
  return response.document;
}

export async function updateDocument(
  companyId: string,
  documentId: string,
  updates: any,
) {
  const response = await apiClient.put(
    `/api/companies/${companyId}/documents/${documentId}`,
    updates,
  );
  return response.document;
}

export async function linkDocuments(
  companyId: string,
  documentId: string,
  linkData: any,
) {
  const response = await apiClient.post(
    `/api/companies/${companyId}/documents/${documentId}/links`,
    linkData,
  );
  return response.document;
}

export async function createCreditNote(
  companyId: string,
  invoiceId: string,
  creditData: any,
) {
  const response = await apiClient.post(
    `/api/companies/${companyId}/invoices/${invoiceId}/credit-notes`,
    creditData,
  );
  return response.document;
}

export async function createInvoiceFromReceipt(
  companyId: string,
  receiptId: string,
) {
  const response = await apiClient.post(
    `/api/companies/${companyId}/receipts/${receiptId}/invoices`,
    {},
  );
  return response.document;
}

export async function getDocumentStats(
  companyId: string,
  filters: { outletId?: string; dateFrom?: string; dateTo?: string } = {},
) {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, value.toString());
    }
  });

  const response = await apiClient.get(
    `/api/companies/${companyId}/documents/stats?${params.toString()}`,
  );
  return response.stats;
}

export async function generatePdf(companyId: string, documentId: string) {
  const token = localStorage.getItem("accessToken");
  const response = await fetch(
    `${API_BASE_URL}/api/companies/${companyId}/documents/${documentId}/pdf`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    handleUnauthorizedResponse(response.status, err?.error?.message);
    throw new Error(
      err?.error?.message || `PDF generation failed (${response.status})`,
    );
  }
  return response.blob();
}

export async function downloadPdf(
  companyId: string,
  documentId: string,
): Promise<void> {
  const token = localStorage.getItem("accessToken");
  const response = await fetch(
    `${API_BASE_URL}/api/companies/${companyId}/documents/${documentId}/pdf`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    handleUnauthorizedResponse(response.status, err?.error?.message);
    throw new Error(
      err?.error?.message || `PDF download failed (${response.status})`,
    );
  }

  const disposition = response.headers.get("Content-Disposition") || "";
  const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
  const filename = filenameMatch
    ? filenameMatch[1]
    : `document-${documentId}.pdf`;

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export function getPdfDownloadUrl(
  companyId: string,
  documentId: string,
): string {
  const token = localStorage.getItem("accessToken");
  return `${API_BASE_URL}/api/companies/${companyId}/documents/${documentId}/pdf?token=${encodeURIComponent(token || "")}`;
}

export async function sendReceiptByEmail(
    companyId: string,
    documentId: string,
    email: string
): Promise<void> {
    const token = localStorage.getItem("accessToken")
    const response = await fetch(
        `${API_BASE_URL}/api/companies/${companyId}/documents/${documentId}/email`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ email }),
        }
    )
    if (!response.ok) {
        const err = await response.json().catch(() => null)
        throw new Error(
            err?.error?.message || `Failed to send email (${response.status})`
        )
    }
}

export function getPublicReceiptUrl(receiptId: string): string {
    return `${API_BASE_URL}/public/receipts/${receiptId}`
}

export async function getHtmlPreview(companyId: string, documentId: string) {
  const token = localStorage.getItem("accessToken");
  const response = await fetch(
    `${API_BASE_URL}/api/companies/${companyId}/documents/${documentId}/preview`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    handleUnauthorizedResponse(response.status, err?.error?.message);
    throw new Error(
      err?.error?.message || `Preview failed (${response.status})`,
    );
  }
  return response.text();
}

const documentsService = {
    searchDocuments,
    getDocument,
    createDocument,
    updateDocument,
    linkDocuments,
    createCreditNote,
    createInvoiceFromReceipt,
    getDocumentStats,
    generatePdf,
    downloadPdf,
    getPdfDownloadUrl,
    sendReceiptByEmail,
    getPublicReceiptUrl,
    getHtmlPreview,
}

export default documentsService;
